import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { LoadingSkeleton } from "@strivelabs/ui-blocks";
import { cn } from "@/lib/utils";

export interface HtmlIframePreviewProps {
  /** Remote URL to stream into the iframe. Mutually exclusive with `srcDoc`. */
  src?: string;
  /** Inline HTML document to render. Mutually exclusive with `src`. */
  srcDoc?: string;
  title: string;
  className?: string;
}

/**
 * Sandboxed iframe wrapper for HTML reports.
 *
 * Sandbox is `allow-scripts` only — no `allow-same-origin` — so the iframe
 * runs in an opaque origin and cannot read parent cookies/localStorage/DOM.
 * The ref is forwarded so a parent (e.g. `useHtmlReportBridge`) can hook
 * onto the iframe element for `postMessage`-based protocols.
 *
 * Use `src` when you have a hosted URL (e.g. `insight.html_url`) — the
 * browser streams the document directly. Use `srcDoc` when the host has
 * the HTML body in memory (e.g. fetched via `GET /insight/:id/html`).
 */
export const HtmlIframePreview = forwardRef<HTMLIFrameElement, HtmlIframePreviewProps>(
  function HtmlIframePreview({ src, srcDoc, title, className }, ref) {
    const innerRef = useRef<HTMLIFrameElement>(null);
    useImperativeHandle(ref, () => innerRef.current as HTMLIFrameElement, []);
    const [loaded, setLoaded] = useState(false);

    return (
      <div className={cn("relative h-full w-full overflow-hidden bg-white", className)}>
        {!loaded && (
          <div className="absolute inset-0 p-6">
            <LoadingSkeleton />
          </div>
        )}
        <iframe
          ref={innerRef}
          src={src}
          srcDoc={srcDoc}
          title={title}
          sandbox="allow-scripts"
          referrerPolicy="no-referrer"
          onLoad={() => setLoaded(true)}
          className="h-full w-full border-0"
        />
      </div>
    );
  }
);
